import {AnchoredOverlay, Button} from '@primer/react'
import {useMemo, useState} from 'react'

import {TriageAgentStatusIcon} from './TriageAgentStatusIcon'
import styles from './TriageAgentButton.module.css'
import {TriageAgentOverlayContent} from './TriageAgentOverlayContent'
import {useTriageAgentStatus} from './use-triage-agent-status'
import {TRIAGE_BUTTON_TEXT, type TriageState} from './utils/constants'

type TriageAgentButtonProps = {
  repoOwner: string
  repoName: string
}

const INDICATOR_CONFIG: Record<TriageState, {testId: string; ariaLabel: string; color: string}> = {
  inactive: {
    testId: 'triage-agent-status-inactive',
    ariaLabel: 'Triage agent is inactive',
    color: 'var(--borderColor-default)',
  },
  active: {
    testId: 'triage-agent-status-active',
    ariaLabel: 'Triage agent is active',
    color: 'var(--fgColor-success)',
  },
}

const ERROR_CONFIG = {
  testId: 'triage-agent-status-error',
  ariaLabel: 'Error loading triage agent status',
  color: 'var(--fgColor-attention)',
}

function TriageAgentButtonLoading() {
  return (
    <Button disabled data-testid="triage-agent-button-loading">
      Triage agent
    </Button>
  )
}

function TriageAgentButtonLoaded({
  repoOwner,
  repoName,
  triageEnabled,
  isError,
}: {
  repoOwner: string
  repoName: string
  triageEnabled: boolean
  isError: boolean
}) {
  const [isOpen, setIsOpen] = useState(false)
  const config = isError ? ERROR_CONFIG : INDICATOR_CONFIG[triageEnabled ? 'active' : 'inactive']

  const leadingVisual = useMemo(
    () => (
      <span
        data-testid={config.testId}
        className={`${styles.statusIndicator} ${triageEnabled ? styles.pulse : ''}`}
        style={{color: config.color}}
      >
        <TriageAgentStatusIcon
          state={triageEnabled ? 'active' : 'inactive'}
          ariaLabel={config.ariaLabel}
          isError={isError}
        />
      </span>
    ),
    [config, triageEnabled, isError],
  )

  return (
    <AnchoredOverlay
      open={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      renderAnchor={props => (
        <Button {...props} leadingVisual={leadingVisual}>
          {TRIAGE_BUTTON_TEXT[triageEnabled ? 'active' : 'inactive']}
        </Button>
      )}
    >
      <TriageAgentOverlayContent repoOwner={repoOwner} repoName={repoName} onClose={() => setIsOpen(false)} />
    </AnchoredOverlay>
  )
}

export function TriageAgentButton({repoOwner, repoName}: TriageAgentButtonProps) {
  const {data, isLoading, isError} = useTriageAgentStatus(repoOwner, repoName)

  if (isLoading) {
    return <TriageAgentButtonLoading />
  }

  const triageEnabled = !!data?.enabled

  return (
    <TriageAgentButtonLoaded
      repoOwner={repoOwner}
      repoName={repoName}
      triageEnabled={triageEnabled}
      isError={isError}
    />
  )
}
